import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretUpIcon,
  FlaskIcon,
  MoonIcon,
  QuestionIcon,
  SunIcon,
  TargetIcon,
} from '@phosphor-icons/react'
import { type Theme, typography, useTheme } from '@pixee/design-system'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as HoverCard from '@radix-ui/react-hover-card'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { match } from 'ts-pattern'
import { LOGOUT_PATH } from '../constants.ts'
import { getNavSection, Routes } from '../routes.ts'
import { useToastService } from '../services/toast-service.tsx'
import { ApiError } from '../utils/api-client/api-error.ts'
import {
  useApiRoot,
  useCurrentUser,
  useIsAdmin,
  usePostSupportBundle,
  useSupportBundleStream,
} from '../utils/api-client/api-hooks.ts'
import {
  type CurrentUserResponse,
  SupportBundleAlreadyRunningProblemResponseSchema,
} from '../utils/api-client/api-schemas.ts'
import { type EnvironmentData, getEnvironmentData } from '../utils/hooks/get-environment-data.ts'
import { getRuntimeEnv } from '../utils/runtime-env.ts'
import { clearExpiredSession } from '../utils/session-state.ts'
import { AdminBadge } from './badges/admin-badge.tsx'
import { NavBarBrandLogo } from './nav-bar-brand-logo.tsx'
import * as styles from './navigation-bar.css.ts'

const SUPPORT_BUNDLE_FALLBACK_RECOURSE = 'See the cluster-shell fallback in the Support Bundles docs.'

/**
 * Foresight's path inside Forge — the one Labs destination that exists today, and so also where the
 * Labs entry itself points.
 *
 * Forge's own route table is always `/foresight/*`; any `/forge` prefix comes from the `BASE_PATH`
 * its backend injects at serve time, which makes it a fact about the deployment rather than about
 * Forge. It therefore belongs to `LABS_URL`, not here, or this composes correctly only on the
 * deployments that happen to mount Forge at `/forge`.
 */
const FORESIGHT_PATH = '/foresight'

/**
 * What Labs drops down to, in the order Forge's own nav lists them.
 *
 * Foresight alone for now. Forge's menu is designed with Vantage and Beacon beside it, and each will
 * carry its own path off `LABS_URL` rather than hanging off Foresight's — which is why the path lives
 * on the item instead of being derived from one base.
 */
const LABS_SUBNAV_ITEMS = [
  { label: 'Foresight', description: 'Design Analysis & Tracking', path: FORESIGHT_PATH, Icon: TargetIcon },
] as const

/**
 * Labs, plus the submenu of Forge destinations behind it.
 *
 * A hover card rather than a dropdown menu: the entry is a link first, and its menu previews where
 * else that link could go — which is what `HoverCard` is for, and it opens on focus as well as hover
 * so the rows are reachable without a pointer. If Labs ever becomes the *only* way to reach these
 * pages, this wants Radix's `NavigationMenu` and real menu semantics instead.
 */
const LabsNavItem: React.FC<{ labsUrl: string }> = ({ labsUrl }) => (
  <HoverCard.Root openDelay={120} closeDelay={120}>
    <HoverCard.Trigger asChild>
      <a href={`${labsUrl}${FORESIGHT_PATH}`} className={classNames(styles.navLink, styles.navLinkWithIcon)}>
        <FlaskIcon size={16} weight="regular" />
        Labs
      </a>
    </HoverCard.Trigger>
    <HoverCard.Portal>
      <HoverCard.Content side="bottom" align="start" sideOffset={8} className={styles.hoverContent}>
        <div className={styles.labsMenu}>
          {LABS_SUBNAV_ITEMS.map(({ label, description, path, Icon }) => (
            <a key={path} href={`${labsUrl}${path}`} className={styles.labsMenuItem}>
              <Icon size={20} weight="regular" className={styles.labsMenuItemIcon} />
              <span className={styles.labsMenuItemText}>
                <span className={styles.labsMenuItemLabel}>{label}</span>
                <span className={styles.labsMenuItemDescription}>{description}</span>
              </span>
            </a>
          ))}
        </div>
      </HoverCard.Content>
    </HoverCard.Portal>
  </HoverCard.Root>
)

export function NavigationBar() {
  const { theme, toggleTheme } = useTheme()
  const { data: user } = useCurrentUser()
  const isAdmin = useIsAdmin()
  const { data: apiRoot } = useApiRoot()
  const environmentData = getEnvironmentData()
  const { pathname } = useLocation()
  const { addToast } = useToastService()

  // Capability discovery: render the menu item only when the platform advertises the link. The
  // chart-values opt-out (or capability-flag-off install) drops the link from the root response.
  const supportBundlesEnabled = apiRoot?._links?.['support-bundles']?.href !== undefined

  // The Dashboards tab is driven by deploy-time runtime config (window.__ENV__), not the API root —
  // see ISS-8153. It appears only when dashboards are enabled for this install; the URL is where
  // dashboards are served on this domain, which the tab navigates to directly (already SSO-authed).
  const { areDashboardsEnabled, dashboardsUrl: runtimeDashboardsUrl, isLabsEnabled, labsUrl } = getRuntimeEnv()
  const dashboardsUrl = areDashboardsEnabled ? runtimeDashboardsUrl : undefined

  // The id returned by the 202 POST. While non-null, the SSE stream is open and the menu item is
  // disabled. Cleared when the SSE stream reaches a terminal phase.
  const [activeBundleId, setActiveBundleId] = useState<string | undefined>(undefined)

  const { mutate: triggerSupportBundle, isPending: isPostPending } = usePostSupportBundle({
    onSuccess: data => {
      setActiveBundleId(data.id)
      addToast({
        message: <>Support bundle started. Pixee will be notified when it's ready.</>,
        variant: 'info',
      })
    },
    onError: error => {
      if (error instanceof ApiError && error.status === 409) {
        const submitter = extractSubmitter(error)
        addToast({
          message: submitter ? (
            <>Another support bundle is already in progress, started by {submitter}.</>
          ) : (
            <>Another support bundle is already in progress.</>
          ),
          variant: 'warning',
        })
      } else {
        addToast({
          message: <>Failed to start support bundle. {SUPPORT_BUNDLE_FALLBACK_RECOURSE}</>,
          variant: 'error',
        })
      }
    },
  })

  // Subscribe to the SSE phase stream once we have a bundle id. The hook is idle while the id is
  // undefined, so this is a no-op pre-POST and post-terminal.
  const sseStream = useSupportBundleStream(activeBundleId)

  // Drive toasts off each SSE phase event. Dedupe by phase value so SSE reconnects (which replay
  // every known phase per the technical-spec) don't re-toast phases the user has already seen.
  const toastedPhasesRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const event of sseStream.events) {
      if (toastedPhasesRef.current.has(event.phase)) continue
      toastedPhasesRef.current.add(event.phase)
      match(event.phase)
        .with('running', () => addToast({ message: <>Generating support bundle…</>, variant: 'info' }))
        .with('succeeded', () => addToast({ message: <>Support bundle delivered to Pixee.</>, variant: 'success' }))
        .with('failed', () =>
          addToast({
            message: <>Support bundle failed. {SUPPORT_BUNDLE_FALLBACK_RECOURSE}</>,
            variant: 'error',
          })
        )
        .otherwise(() => undefined)
    }
  }, [sseStream.events, addToast])

  // Clear the active id on terminal so the menu item re-enables and the SSE hook tears down.
  useEffect(() => {
    if (sseStream.connectionStatus === 'finished') {
      setActiveBundleId(undefined)
      toastedPhasesRef.current = new Set()
    }
  }, [sseStream.connectionStatus])

  const isGeneratingSupportBundle = isPostPending || activeBundleId !== undefined

  return (
    <NavigationBarPure
      environmentData={environmentData}
      user={user}
      isAdmin={isAdmin}
      pathname={pathname}
      theme={theme}
      toggleTheme={toggleTheme}
      userProfileUrl={user?.account_management_url ?? undefined}
      dashboardsUrl={dashboardsUrl}
      isLabsEnabled={isLabsEnabled}
      labsUrl={labsUrl}
      onGenerateSupportBundle={supportBundlesEnabled ? () => triggerSupportBundle() : undefined}
      isGeneratingSupportBundle={isGeneratingSupportBundle}
    />
  )
}

function extractSubmitter(error: ApiError): string | undefined {
  if (!error.bodyAsText) return undefined
  try {
    const parsed: unknown = JSON.parse(error.bodyAsText)
    const result = SupportBundleAlreadyRunningProblemResponseSchema.safeParse(parsed)
    return result.success ? result.data.submitter : undefined
  } catch {
    return undefined
  }
}

export type NavigationBarPureProps = {
  environmentData: EnvironmentData
  user?: CurrentUserResponse
  /** `undefined` until the role is known — see `useIsAdmin`. */
  isAdmin?: boolean
  pathname: string
  theme: Theme
  toggleTheme: () => unknown
  userProfileUrl?: string
  dashboardsUrl?: string
  isLabsEnabled?: boolean
  labsUrl?: string
  onGenerateSupportBundle?: () => void
  isGeneratingSupportBundle?: boolean
}
export const NavigationBarPure: React.FC<NavigationBarPureProps> = ({
  environmentData,
  user,
  isAdmin,
  pathname,
  theme,
  toggleTheme,
  userProfileUrl,
  dashboardsUrl,
  isLabsEnabled = false,
  labsUrl,
  onGenerateSupportBundle,
  isGeneratingSupportBundle = false,
}) => {
  const avatarProps = match(environmentData.name)
    .with('ntt', () => ({
      src: environmentData.logoIconHref,
      style: { height: '28px', marginRight: '-8px', borderRadius: '50%' },
      alt: environmentData.logoIconAlt,
    }))
    .otherwise(() => ({
      src: user?.avatar_url,
      style: { width: '28px', height: '28px', marginRight: '8px', borderRadius: '50%' },
      alt: `${user?.username ?? user?.login}'s profile image`,
    }))

  const navSection = useMemo(() => getNavSection(pathname), [pathname])
  const isRepositoriesPage = navSection === 'repositories'
  const isScansPage = navSection === 'scans'
  const isOrganizationPage = navSection === 'organization'
  const isSecurityPosturePage = navSection === 'security-posture'

  return (
    <nav className={styles.navbar} aria-label="Main Navigation">
      <div className={styles.leftGroup}>
        <Link to="/">
          <NavBarBrandLogo environmentData={environmentData} theme={theme} />
        </Link>
        {user && isLabsEnabled && (
          <Link
            to={Routes.SecurityPosturePage.path}
            className={classNames(styles.navLink, {
              [styles.activeNavLink]: isSecurityPosturePage,
            })}
          >
            Security posture
          </Link>
        )}
        {user && (
          <Link
            to={Routes.OrganizationPage.path}
            className={classNames(styles.navLink, {
              [styles.activeNavLink]: isOrganizationPage,
            })}
          >
            Organization
          </Link>
        )}
        {user && (
          <Link
            to="/"
            className={classNames(styles.navLink, {
              [styles.activeNavLink]: isRepositoriesPage,
            })}
          >
            Repositories
          </Link>
        )}
        {user && (
          <Link
            to="/scans/"
            className={classNames(styles.navLink, {
              [styles.activeNavLink]: isScansPage,
            })}
          >
            Scans
          </Link>
        )}
        {/* These two differ on purpose. Dashboards opens in its own tab: Superset is a separate
            application with its own session and navigation, and sending the reader there in place
            discards whatever they had in progress here. Labs does not, because Forge is mounted
            under this same origin and reads as another part of one product rather than a trip out
            of it — a new tab per visit would pile up windows inside a single surface. */}
        {user && dashboardsUrl && (
          <a href={dashboardsUrl} className={classNames(styles.navLink, styles.navLinkWithIcon)} target="_blank">
            Dashboards
            <ArrowSquareOutIcon size={12} weight="light" />
          </a>
        )}
        {user && isLabsEnabled && labsUrl && <LabsNavItem labsUrl={labsUrl} />}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
        <a href="https://docs.pixee.ai" target="_blank" className={styles.helpLink}>
          <QuestionIcon size={24} weight="fill" />
        </a>

        <button onClick={toggleTheme} style={{ cursor: 'pointer' }} aria-label="Toggle theme" title="Toggle theme">
          {theme === 'light' ? <SunIcon size={24} weight="regular" /> : <MoonIcon size={24} weight="regular" />}
        </button>

        {user && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className={styles.dropdownMenuTrigger} aria-label="User profile and logout menu">
              <img {...avatarProps} />
              <CaretUpIcon className={styles.caretUpIcon} size={16} weight="fill" />
              <CaretDownIcon className={styles.caretDownIcon} size={16} weight="fill" />
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content className={styles.dropdownMenuContent} sideOffset={17}>
                <DropdownMenu.Label>
                  <p className={typography.paragraph} style={{ margin: 0 }}>
                    Signed in as{' '}
                  </p>
                  <p className={typography.h6} style={{ margin: 0 }}>
                    {(() => {
                      const displayName = user.username ?? user.login ?? ''
                      return displayName.length > 20 ? `${displayName.slice(0, 17)}...` : displayName
                    })()}
                  </p>
                  {/* Only `true` shows the badge: `undefined` means we don't know yet, and a badge
                      that appears a beat late is better than one that appears wrongly. See
                      `useIsAdmin` for what the role means on an install with enforcement off. */}
                  {isAdmin === true && <AdminBadge />}
                </DropdownMenu.Label>
                <DropdownMenu.Separator className={styles.dropdownMenuSeparator} />
                {userProfileUrl && (
                  <DropdownMenu.Item className={styles.dropdownMenuItem} asChild>
                    <a
                      href={userProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'unset', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      User Profile <ArrowSquareOutIcon size={12} weight="light" />
                    </a>
                  </DropdownMenu.Item>
                )}
                {onGenerateSupportBundle && (
                  <DropdownMenu.Item
                    className={styles.dropdownMenuItem}
                    disabled={isGeneratingSupportBundle}
                    onSelect={() => onGenerateSupportBundle()}
                    style={{ cursor: 'pointer' }}
                  >
                    {isGeneratingSupportBundle ? 'Generating support bundle…' : 'Generate Support Bundle'}
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item className={styles.dropdownMenuItem} asChild>
                  <a href={LOGOUT_PATH} onClick={clearExpiredSession} style={{ color: 'unset', display: 'block' }}>
                    Sign Out
                  </a>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </nav>
  )
}
